import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Select, DatePicker, Button, Spin, Tabs, Input } from 'antd';
import { SearchOutlined, LineChartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import http from '../Http';
import { useBatteryData } from './hooks/useBatteryData';
import { computeAllStats } from './utils/statsCalculator';
import BatteryChartPanel from './BatteryChartPanel';
import MeasurementPanel from './MeasurementPanel';
import AnomalyPanel from './AnomalyPanel';
import ResearchPanel from './ResearchPanel';
import ExportToolbar from './ExportToolbar';
import utils from '../utils';
import './BatteryAnalysis.css';

const { RangePicker } = DatePicker;

/**
 * Transform raw API rows into chart-friendly format expected by BatteryChartPanel.
 */
function transformRowsToChartData(rows) {
  if (!rows || rows.length === 0) return null;

  // Backend may return numeric fields as strings after Scale conversion (e.g. "3.2").
  // toNum keeps Number() usage compatible for both numbers and numeric strings.
  const toNum = (v) => (v != null && v !== '' ? Number(v) : null);

  const timestamps = [];
  const packVoltage = [];
  const maxCellVoltage = [];
  const minCellVoltage = [];
  const soc = [];
  const current = [];
  const status = [];
  const cellDifference = [];

  // Collect unique temperature keys
  const temperatureKeys = [];
  if (rows[0]) {
    Object.keys(rows[0]).forEach(k => {
      if (/^Temp\d+$/.test(k) || k === 'MaxT' || k === 'MinT') {
        if (!temperatureKeys.includes(k)) temperatureKeys.push(k);
      }
    });
  }
  const tempArrays = temperatureKeys.map(() => []);

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    timestamps.push(row.CreateTime || row.Time || '');
    packVoltage.push(toNum(row.Voltage));
    soc.push(toNum(row.SOC));
    current.push(toNum(row.Current));
    status.push(toNum(row.Status));

    // Temperature
    for (let ti = 0; ti < temperatureKeys.length; ti++) {
      tempArrays[ti].push(toNum(row[temperatureKeys[ti]]));
    }

    // Max / Min cell voltage (already converted to V by the backend)
    const maxCV = toNum(row.MaxCellVoltage);
    const minCV = toNum(row.MinCellVoltage);
    maxCellVoltage.push(maxCV);
    minCellVoltage.push(minCV);

    // Cell difference (MaxCellVoltage - MinCellVoltage) in mV
    if (maxCV != null && minCV != null) {
      cellDifference.push(Math.round((maxCV - minCV) * 1000));
    } else {
      cellDifference.push(null);
    }
  }

  return {
    timestamps,
    packVoltage,
    maxCellVoltage,
    minCellVoltage,
    soc,
    current,
    status,
    temperatures: tempArrays,
    temperatureKeys,
    cellDifference,
  };
}

export default function BatteryAnalysis({ onNavigate }) {
  const { t } = useTranslation();
  const setTip = useContext(utils.tipContext);

  // Device search state
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [deviceSelectOptions, setDeviceSelectOptions] = useState([]);
  const refTimerDeviceSearcher = useRef(null);
  const refDeviceSearchValue = useRef(null);

  // Date range state
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, 'days'),
    moment()
  ]);

  // Query trigger state
  const [queryParams, setQueryParams] = useState({ deviceID: null, dateRange: null });

  // Use battery data hook for pagination + downsampling
  const {
    data: hookData,
    chartData: hookChartData,
    loading,
    error: hookError,
    total,
    loadMore,
    hasMore,
    anomalies: hookAnomalies,
    stats: hookStats,
  } = useBatteryData(queryParams.deviceID, queryParams.dateRange);

  // Transformed data for chart
  const rawData = hookData && hookData.length > 0 ? transformRowsToChartData(hookChartData) : null;
  const chartData = rawData;

  // Marker state
  const [measureMarkers, setMeasureMarkers] = useState([]);
  const [eventMarkers, setEventMarkers] = useState([]);
  const [isMarkerMode, setIsMarkerMode] = useState(false);
  const [markerStats, setMarkerStats] = useState(null);

  // Side panel active tab
  const [activeTab, setActiveTab] = useState('measurement');

  // Chart ref for export
  const chartRef = useRef(null);

  // Device search handler (debounced, following Curve.js pattern)
  const handleSearchDevice = (v) => {
    if (refTimerDeviceSearcher.current != null) {
      clearTimeout(refTimerDeviceSearcher.current);
    }
    refDeviceSearchValue.current = v;

    refTimerDeviceSearcher.current = setTimeout(async () => {
      const resp = await http.post("device/id-name/list", {
        SN: v,
      }).then(async (r) => {
        return await r.json();
      }).catch((e) => {
        console.log("[BatteryAnalysis]", e);
        return null;
      });

      if (refDeviceSearchValue.current !== v) {
        return;
      }

      try {
        if (!resp || resp.code !== 200) {
          if (setTip) setTip({
            open: true,
            severity: "error",
            msg: (resp && resp.msg) ? resp.msg : t('load_device_list_error')
          });
          return;
        }

        const ls = resp.data.map(({ SN, ID }) => (
          <Select.Option key={ID} value={ID} data={{ ID, SN }}>{SN}</Select.Option>
        ));
        setDeviceSelectOptions(ls);
      } catch (e) {
        console.log("[BatteryAnalysis]", e);
        if (setTip) setTip({
          open: true,
          severity: "error",
          msg: t('load_device_list_error')
        });
      }
    }, 300);
  };

  // Trigger analysis via hook by updating queryParams
  const handleAnalyze = useCallback(() => {
    if (!deviceInfo) {
      if (setTip) setTip({ open: true, severity: "warning", msg: t('please_select_device') });
      return;
    }
    if (!dateRange || dateRange.length !== 2) {
      if (setTip) setTip({ open: true, severity: "warning", msg: t('please_select_date_range') });
      return;
    }

    setMeasureMarkers([]);
    setEventMarkers([]);
    setMarkerStats(null);
    setQueryParams({ deviceID: deviceInfo.ID, dateRange: [...dateRange] });
  }, [deviceInfo, dateRange, setTip, t]);

  // Show hook errors as tips
  useEffect(() => {
    if (hookError && setTip) {
      setTip({ open: true, severity: "error", msg: hookError });
    }
  }, [hookError, setTip]);

  // Recompute marker stats when markers change
  useEffect(() => {
    if (measureMarkers.length >= 2 && hookData && hookData.length > 0) {
      const indices = measureMarkers.map(m => m.dataIndex).filter(idx => idx != null);
      const result = computeAllStats(hookData, indices);
      setMarkerStats(result);
    } else {
      setMarkerStats(null);
    }
  }, [measureMarkers, hookData]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (refTimerDeviceSearcher.current != null) {
        clearTimeout(refTimerDeviceSearcher.current);
      }
    };
  }, []);

  // Handle chart click for placing markers
  const handleChartClick = useCallback((params) => {
    if (!isMarkerMode) return;

    const timestamp = params.value ? params.value[0] : params.name;
    const newMarker = {
      id: `M${measureMarkers.length + 1}`,
      label: `M${measureMarkers.length + 1}`,
      timestamp: timestamp,
      dataIndex: params.dataIndex,
    };
    setMeasureMarkers(prev => [...prev, newMarker]);
  }, [isMarkerMode, measureMarkers.length]);

  // Remove a specific marker
  const handleRemoveMarker = useCallback((markerId) => {
    setMeasureMarkers(prev => prev.filter(m => m.id !== markerId));
  }, []);

  // Clear all markers
  const handleClearMarkers = useCallback(() => {
    setMeasureMarkers([]);
    setMarkerStats(null);
  }, []);

  // Add event marker from anomaly detection
  const handleEventMarkerAdd = useCallback((anomaly) => {
    const newMarker = {
      id: `E${eventMarkers.length + 1}`,
      timestamp: anomaly.timestamp,
      type: anomaly.type,
      severity: anomaly.severity,
    };
    setEventMarkers(prev => [...prev, newMarker]);
  }, [eventMarkers.length]);

  // Side panel tabs
  const panelTabs = [
    {
      key: 'measurement',
      tab: t('Measurement'),
      children: (
        <MeasurementPanel
          markers={measureMarkers}
          stats={markerStats}
          onAddMarker={() => setIsMarkerMode(true)}
          onRemoveMarker={handleRemoveMarker}
          onClearMarkers={handleClearMarkers}
          isMarkerMode={isMarkerMode}
          onToggleMarkerMode={() => setIsMarkerMode(prev => !prev)}
        />
      ),
    },
    {
      key: 'anomaly',
      tab: t('Anomaly Detection'),
      children: (
        <AnomalyPanel
          deviceID={deviceInfo ? deviceInfo.ID : null}
          dateRange={dateRange}
          onEventMarkerAdd={handleEventMarkerAdd}
        />
      ),
    },
    {
      key: 'research',
      tab: t('Research'),
      children: (
        <ResearchPanel
          data={rawData}
          anomalies={eventMarkers}
        />
      ),
    },
  ];

  return (
    <div className="battery-analysis-container">
      {/* Toolbar */}
      <div className="battery-analysis-toolbar">
        <span className="battery-analysis-toolbar-title">{t('Battery Analysis')}</span>

        <Input.Group compact>
          <Button>{t('Device SN')}</Button>
          <Select
            style={{ minWidth: 280 }}
            showSearch
            defaultActiveFirstOption={false}
            showArrow={false}
            filterOption={false}
            onSearch={handleSearchDevice}
            onSelect={(value, option) => {
              setDeviceInfo(option.data);
            }}
            placeholder={t('Search device...')}
          >
            {deviceSelectOptions}
          </Select>
        </Input.Group>

        <RangePicker
          showTime
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          style={{ minWidth: 340 }}
        />

        <Button
          type="primary"
          icon={<LineChartOutlined />}
          onClick={handleAnalyze}
          loading={loading}
        >
          {t('Analyze')}
        </Button>
      </div>

      {/* Main content area */}
      <div className="battery-analysis-content">
        {/* Chart area (left ~75%) */}
        <div className={`battery-analysis-chart-area ${isMarkerMode ? 'battery-analysis-marker-mode-active' : ''}`}>
          <BatteryChartPanel
            ref={chartRef}
            data={rawData}
            chartData={chartData}
            measureMarkers={measureMarkers}
            eventMarkers={eventMarkers}
            onChartClick={handleChartClick}
            onChartReady={() => {}}
            loading={loading}
          />
        </div>

        {/* Side panel (right ~25%) */}
        <div className="battery-analysis-side-panel">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="small"
            items={panelTabs.map(tab => ({
              key: tab.key,
              label: tab.tab,
              children: tab.children,
            }))}
          />
        </div>
      </div>

      {/* Load more button */}
      {hasMore && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Button onClick={loadMore} loading={loading}>
            {t('Load More')}
          </Button>
        </div>
      )}

      {/* Export toolbar (bottom) */}
      <ExportToolbar chartRef={chartRef} data={rawData} />
    </div>
  );
}
